import { Close, Content, Overlay, Portal, Root, Trigger } from '@radix-ui/react-dialog';
import Button from '@/components/elements/Button';
import { LinkRenderer, findHref, isValidLink } from '@/components/elements/Link';
import { IComponentSharedLink, Maybe } from '@/graphql/__generated__/global/types';
import classNames from '@/lib/classNames';
import { TRACKING_EVENT, track } from '@/lib/gtm';
import { m as motion } from 'framer-motion';
import { useRouter } from 'next/router';
import BookingSearchBar from './BookingSearchBar';

export const BookingWidget = ({
    className,
    showMobileBookingBar = true,
    secondaryCta,
    bookingBarText,
}: {
    className?: string;
    showMobileBookingBar?: boolean;
    secondaryCta?: Maybe<IComponentSharedLink>;
    bookingBarText?: string;
}) => {
    const router = useRouter();

    if (!showMobileBookingBar) {
        return null;
    }

    return (
        <>
            {/* Start Desktop */}
            <div className="hidden lg:block">
                <BookingSearchBar className={classNames(className, 'hidden lg:block')} bookingBarText={bookingBarText} />
            </div>
            {/* End Desktop */}
            {/* Start Mobile */}
            <div className="block lg:hidden">
                <Root>
                    <motion.div
                        className={classNames(className, 'lg:hidden flex flex-col items-center justify-center gap-4 bottom-12 relative')}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.5,
                            delay: 0.5,
                            ease: 'easeIn',
                        }}
                    >
                        <Trigger className={classNames(className)} asChild>
                            <Button variant="white" className="w-64 h-16 mx-auto border-white hover:border-transparent">
                                Book Now
                            </Button>
                        </Trigger>
                        {secondaryCta && isValidLink(secondaryCta.linkToPage, secondaryCta.linkToStory, secondaryCta.linkToExternalPage) && (
                            <LinkRenderer
                                {...secondaryCta}
                                onClick={() =>
                                    track(TRACKING_EVENT.PRESSED_BUTTON, {
                                        label: secondaryCta.label,
                                        category: 'Hero Video',
                                        url: findHref(secondaryCta.linkToPage, secondaryCta.linkToStory, secondaryCta.linkToExternalPage),
                                        page: router.asPath,
                                    })
                                }
                            >
                                <Button variant="outline" colour="white" className="w-64 h-16 mx-auto hover:border-transparent">
                                    {secondaryCta.label}
                                </Button>
                            </LinkRenderer>
                        )}
                    </motion.div>
                    <Portal>
                        <Overlay className="bg-black/70 data-[state=open]:animate-overlayShow fixed inset-0 z-50" />
                        <Content className="flex flex-col gap-6 data-[state=open]:animate-contentShow fixed z-50 top-[50%] left-[50%] w-full max-w-[calc(100vw_-_2rem)] translate-x-[-50%] translate-y-[-50%] bg-white px-0 md:px-2 rounded-3xl py-10 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none">
                            <BookingSearchBar bookingBarText={bookingBarText} />
                            <Close asChild>
                                <Button variant="ghost" className="text-primary bg-shell-25" aria-label="Close" size="xl">
                                    Close X
                                </Button>
                            </Close>
                        </Content>
                    </Portal>
                </Root>
            </div>
            {/* End Mobile */}
        </>
    );
};

export default BookingWidget;
